import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, ExternalLink } from "lucide-react";

/**
 * Reusable manual-enrichment dialog.
 * Pass EITHER (propertyId + address hint) OR (leadId + address hint).
 * Data is saved to db.skip_trace_cache keyed by normalized address, so a
 * future lookup on ANY lead / property with the same house hits the cache.
 */
export default function SkipPasteDialog({
  open, onOpenChange,
  address = "",
  ownerName = "",
  propertyId = null,
  leadId = null,
  onSaved,
}) {
  const [phones, setPhones] = useState("");
  const [emails, setEmails] = useState("");
  const [relatives, setRelatives] = useState("");
  const [mailing, setMailing] = useState("");
  const [busy, setBusy] = useState(false);

  const splitLines = (s) => s
    .split(/[\n,;]/)
    .map((x) => x.trim())
    .filter(Boolean);

  const save = async () => {
    const payload = {
      property_id: propertyId,
      lead_id: leadId,
      address: address || null,
      owner_name: ownerName || null,
      data: {
        phones: splitLines(phones),
        emails: splitLines(emails),
        relatives: splitLines(relatives),
        mailing_addresses: splitLines(mailing),
      },
    };
    if (!payload.address && !payload.property_id && !payload.lead_id) {
      toast.error("Need an address, property, or lead to attach to.");
      return;
    }
    if (payload.data.phones.length + payload.data.emails.length + payload.data.relatives.length === 0) {
      toast.error("Paste at least one phone, email, or relative.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/skip-trace/manual", payload);
      toast.success("Cached — future lookups are free");
      setPhones(""); setEmails(""); setRelatives(""); setMailing("");
      onOpenChange(false);
      onSaved && onSaved(data.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const tps = `https://www.truepeoplesearch.com/results?citystatezip=${encodeURIComponent(address || "")}${ownerName ? `&name=${encodeURIComponent(ownerName)}` : ""}`;
  const fps = `https://www.fastpeoplesearch.com/name/${encodeURIComponent((ownerName || "").replace(/\s+/g, "-"))}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="skip-paste-dialog">
        <DialogHeader>
          <DialogTitle>Add Skip Data</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Paste what you found — we&apos;ll cache it under this address so
            every future lookup (any lead, any property) is instant + free.
          </DialogDescription>
        </DialogHeader>

        <div className="text-[11px] text-muted-foreground border-l-2 border-[var(--app-accent)] pl-2 space-y-1">
          <div>For: <span className="font-medium">{address || "(no address given)"}</span></div>
          {ownerName && <div>Owner hint: <span className="font-medium">{ownerName}</span></div>}
          <div className="pt-1 flex flex-wrap gap-2">
            <a href={tps} target="_blank" rel="noreferrer"
               className="text-[var(--app-accent)] hover:underline"
               data-testid="skip-tps-link">
              Search TruePeopleSearch <ExternalLink size={9} className="inline" />
            </a>
            {ownerName && (
              <a href={fps} target="_blank" rel="noreferrer"
                 className="text-[var(--app-accent)] hover:underline"
                 data-testid="skip-fps-link">
                Search FastPeopleSearch <ExternalLink size={9} className="inline" />
              </a>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Phone numbers <span className="text-muted-foreground">(one per line or comma-separated)</span></Label>
            <Textarea rows={3} value={phones} onChange={(e) => setPhones(e.target.value)}
                      placeholder="(305) 555-1111&#10;(305) 555-2222"
                      className="mt-1 font-mono text-xs"
                      data-testid="skip-paste-phones" />
          </div>
          <div>
            <Label className="text-xs">Emails</Label>
            <Textarea rows={2} value={emails} onChange={(e) => setEmails(e.target.value)}
                      placeholder="john@example.com"
                      className="mt-1 font-mono text-xs"
                      data-testid="skip-paste-emails" />
          </div>
          <div>
            <Label className="text-xs">Relatives</Label>
            <Textarea rows={2} value={relatives} onChange={(e) => setRelatives(e.target.value)}
                      placeholder="Jane Smith, Bob Smith"
                      className="mt-1 text-xs"
                      data-testid="skip-paste-relatives" />
          </div>
          <div>
            <Label className="text-xs">Other mailing addresses</Label>
            <Input value={mailing} onChange={(e) => setMailing(e.target.value)}
                   placeholder="PO Box 123, Miami FL"
                   className="mt-1 text-xs"
                   data-testid="skip-paste-mailing" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
            data-testid="skip-paste-save"
          >
            {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Save size={13} className="mr-2" />}
            Save to Cache
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
